// Main App component for InfluenceHub MVP
// Root component that sets up routing and authentication context
// Handles overall application state and navigation

import React, { useState, useEffect } from 'react';
import RoleGuard from './components/RoleGuard';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Onboarding from './pages/Onboarding';
import OnboardingStep1 from './pages/onboarding/Step1';
import OnboardingStep2 from './pages/onboarding/Step2';
import OnboardingStep3 from './pages/onboarding/Step3';
import BrandOnboarding from './pages/onboarding/BrandOnboarding';
import BrandDashboard from './pages/BrandDashboard';
import BrandProfile from './pages/BrandProfile';
import BrandPreferences from './pages/BrandPreferences';
import SelectRole from './pages/SelectRole';
import InfluencerWizard from './pages/signup/influencer/InfluencerWizard';
// Legacy imports - brand/campaign features moved to /legacy
import BrandWizard from './legacy/brand/signup/BrandWizard';
import AdminDashboard from './legacy/admin/AdminDashboard';
import BrandDetail from './legacy/brand/BrandDetail';
import InfluencerDetail from './legacy/admin/InfluencerDetail';
import PendingApproval from './pages/PendingApproval';
import InfluencerPendingApproval from './pages/InfluencerPendingApproval';
import CreateCampaign from './legacy/brand/CreateCampaign';
import CampaignSubmitted from './legacy/campaigns/CampaignSubmitted';
import CampaignDetail from './legacy/campaigns/CampaignDetail';
import MyCampaigns from './legacy/brand/MyCampaigns';
import BrandCampaignDetail from './legacy/brand/BrandCampaignDetail';
import BrandApplications from './legacy/brand/BrandApplications';
import InfluencerApplications from './legacy/applications/InfluencerApplications';
import ExploreCampaigns from './legacy/campaigns/ExploreCampaigns';
import ApplicationDetail from './legacy/applications/ApplicationDetail';
import Settings from './pages/Settings';
import DebugAuth from './pages/DebugAuth';
import TestAuth from './pages/TestAuth';
import DebugAppCheck from './pages/DebugAppCheck';
import ClientFirebaseBootstrap from './components/ClientFirebaseBootstrap';
import ForgotPassword from './pages/ForgotPassword';
// Marketing (Website merge)
import MarketingLayout from './components/marketing/MarketingLayout';
import LandingPage from './components/marketing/LandingPage';
import ContactPage from './components/marketing/ContactPage';
import PrivacyPage from './components/marketing/PrivacyPage';
import TermsPage from './components/marketing/TermsPage';
import WhatIsInfluencerProfilePage from './components/marketing/WhatIsInfluencerProfilePage';
import CreateInfluencerProfilePage from './components/marketing/CreateInfluencerProfilePage';
import MediaKitGuidePage from './components/marketing/MediaKitGuidePage';
import BrandDealCheckerPage from './components/marketing/BrandDealCheckerPage';
// New influencer-first MVP pages
import Dashboard from './app/dashboard/page';
import ProfileEditor from './app/profile/edit/page';
import FeedbackFeedPage from './pages/creatorFeedback/FeedbackFeedPage';
import FeedbackPostDetailPage from './pages/creatorFeedback/FeedbackPostDetailPage';
import ContractToolsPage from './pages/creator/ContractToolsPage';
import PublicProfileWithLayout from './components/layout/PublicProfileWithLayout';
import MediaKit from './app/[username]/media-kit/page';
import EmailUnsubscribePage from './app/email/unsubscribe/page';
import { initGA, pageview } from './analytics/ga';
import { canonicalizeMisroutedFeedbackPath } from './lib/routing/canonicalizeMisroutedFeedbackPath.js';
import './styles/globals.css';

// Compute initial route from URL so first paint shows correct page (avoids white/wrong screen)
function getInitialRouteState() {
  if (typeof window === 'undefined') return { currentPage: 'login', publicUsername: '' };
  canonicalizeMisroutedFeedbackPath();
  const rawPath = window.location.pathname;
  let path = rawPath !== '/' && rawPath.endsWith('/') ? rawPath.slice(0, -1) : rawPath;
  if (!path.startsWith('/')) path = '/' + path;
  if (path === '/brand-deal-checker') {
    window.history.replaceState({}, '', '/influencer-contract-checker' + (window.location.search || ''));
    path = '/influencer-contract-checker';
  }
  if (path.startsWith('/community/profile/')) return { currentPage: 'profile-edit', publicUsername: '' };
  switch (path) {
    case '':
    case '/':
      return { currentPage: 'marketing-landing', publicUsername: '' };
    case '/login':
      return { currentPage: 'login', publicUsername: '' };
    case '/contact':
      return { currentPage: 'marketing-contact', publicUsername: '' };
    case '/privacy':
      return { currentPage: 'marketing-privacy', publicUsername: '' };
    case '/terms':
      return { currentPage: 'marketing-terms', publicUsername: '' };
    case '/what-is-an-influencer-profile':
      return { currentPage: 'marketing-what-is-influencer-profile', publicUsername: '' };
    case '/create-influencer-profile':
      return { currentPage: 'marketing-create-influencer-profile', publicUsername: '' };
    case '/media-kit-guide':
      return { currentPage: 'marketing-media-kit-guide', publicUsername: '' };
    case '/influencer-contract-checker':
      return { currentPage: 'marketing-brand-deal-checker', publicUsername: '' };
    case '/forgot-password':
      return { currentPage: 'forgot-password', publicUsername: '' };
    case '/signup':
      return { currentPage: 'signup', publicUsername: '' };
    case '/onboarding':
      return { currentPage: 'onboarding', publicUsername: '' };
    case '/onboarding/step-1':
      return { currentPage: 'onboarding/step-1', publicUsername: '' };
    case '/onboarding/step-2':
      return { currentPage: 'onboarding/step-2', publicUsername: '' };
    case '/onboarding/step-3':
      return { currentPage: 'onboarding/step-3', publicUsername: '' };
    case '/onboarding/brand':
      return { currentPage: 'onboarding/brand', publicUsername: '' };
    case '/brand/dashboard':
      return { currentPage: 'brand-dashboard', publicUsername: '' };
    case '/brand/preferences':
      return { currentPage: 'brand-preferences', publicUsername: '' };
    case '/brand/profile':
      return { currentPage: 'brand-profile', publicUsername: '' };
    case '/select-role':
      return { currentPage: 'select-role', publicUsername: '' };
    case '/profile/edit':
      return { currentPage: 'profile-edit', publicUsername: '' };
    case '/profile/contract-tools':
      return { currentPage: 'profile-contract-tools', publicUsername: '' };
    case '/feedback': {
      const feedbackSearch = typeof window !== 'undefined' ? window.location.search : '';
      const feedbackPost = feedbackSearch ? new URLSearchParams(feedbackSearch).get('post') : null;
      return {
        currentPage: 'feedback-feed',
        publicUsername: '',
        _feedbackPostId: feedbackPost && feedbackPost.trim() ? feedbackPost.trim() : undefined,
      };
    }
    case '/feedback/new':
      return { currentPage: 'feedback-feed', publicUsername: '' };
    case '/settings':
      return { currentPage: 'settings', publicUsername: '' };
    case '/settings/email':
      return { currentPage: 'email-unsubscribe', publicUsername: '' };
    case '/email/unsubscribe':
      return { currentPage: 'email-unsubscribe', publicUsername: '' };
    case '/brand/create-campaign':
      return { currentPage: 'create-campaign', publicUsername: '' };
    case '/brand/campaigns':
      return { currentPage: 'my-campaigns', publicUsername: '' };
    case '/brand/applications':
      return { currentPage: 'brand-applications', publicUsername: '' };
    case '/influencer/explore-campaigns':
    case '/explore-campaigns':
      return { currentPage: 'explore-campaigns', publicUsername: '' };
    case '/campaign-submitted':
      return { currentPage: 'campaign-submitted', publicUsername: '' };
    case '/debug-auth':
      return { currentPage: 'debug-auth', publicUsername: '' };
    case '/test-auth':
      return { currentPage: 'test-auth', publicUsername: '' };
    case '/debug/appcheck':
      return { currentPage: 'debug-appcheck', publicUsername: '' };
    default:
      if (path.startsWith('/feedback/')) {
        const rest = path.slice(10);
        const q = rest.indexOf('?');
        const segment = q >= 0 ? rest.slice(0, q) : rest;
        if (segment === 'new') return { currentPage: 'feedback-feed', publicUsername: '' };
        if (segment && !segment.includes('/')) {
          return {
            currentPage: 'feedback-detail',
            publicUsername: '',
            _feedbackPostId: segment,
          };
        }
      }
      if (path.startsWith('/brand/campaigns/')) {
        const campaignId = path.split('/brand/campaigns/')[1];
        if (campaignId) return { currentPage: 'brand-campaign-detail', publicUsername: '', _campaignId: campaignId };
      }
      if (path.startsWith('/brand/applications/')) {
        const applicationId = path.split('/brand/applications/')[1];
        if (applicationId) return { currentPage: 'application-detail', publicUsername: '', _applicationId: applicationId };
      }
      if (path.startsWith('/signup-wizard')) return { currentPage: 'signup-wizard', publicUsername: '' };
      if (path.startsWith('/brand-wizard')) return { currentPage: 'brand-wizard', publicUsername: '' };
      if (path.startsWith('/u/')) {
        const parts = path.split('/').filter(Boolean);
        if (parts[2] === 'media-kit' && parts[1]) {
          return { currentPage: 'media-kit', publicUsername: parts[1] };
        }
        const username = parts[1];
        if (username === 'email') return { currentPage: 'email-unsubscribe', publicUsername: '' };
        if (username) return { currentPage: 'public-profile', publicUsername: username };
      }
      if (path.endsWith('/media-kit')) return { currentPage: 'media-kit', publicUsername: '' };
      const isPublicProfilePath = path !== '/' && path !== '/contact' && path !== '/privacy' && path !== '/terms' &&
        path !== '/what-is-an-influencer-profile' && path !== '/create-influencer-profile' && path !== '/media-kit-guide' &&
        path !== '/influencer-contract-checker' &&
        !path.startsWith('/login') && !path.startsWith('/signup') && !path.startsWith('/onboarding') && !path.startsWith('/admin') &&
        !path.startsWith('/brand') && !path.startsWith('/influencer') && !path.startsWith('/campaign') && !path.startsWith('/forgot-password') &&
        !path.startsWith('/select-role') && !path.startsWith('/pending-approval') &&         !path.startsWith('/debug-auth') && !path.startsWith('/test-auth') && !path.startsWith('/debug') &&
        !path.startsWith('/settings') && !path.startsWith('/collabs') && !path.startsWith('/community') && !path.startsWith('/notifications') &&
        !path.startsWith('/feedback') && !path.startsWith('/u/');
      if (isPublicProfilePath) {
        const username = path.split('/').filter(Boolean)[0];
        if (username && username !== 'dashboard' && username !== 'profile' && username !== 'u') {
          return { currentPage: 'public-profile', publicUsername: username };
        }
      }
      return { currentPage: 'login', publicUsername: '' };
  }
}

const initialRoute = getInitialRouteState();

const App = () => {
  // Simple routing state - in a real app, you'd use React Router
  const [currentPage, setCurrentPage] = useState(initialRoute.currentPage);
  const [feedbackPostId, setFeedbackPostId] = useState(
    initialRoute._feedbackPostId ?? null
  );
  const [queryParams, setQueryParams] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [selectedInfluencerId, setSelectedInfluencerId] = useState(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState(initialRoute._campaignId ?? null);
  const [selectedApplicationId, setSelectedApplicationId] = useState(initialRoute._applicationId ?? null);
  const [publicUsername, setPublicUsername] = useState(initialRoute.publicUsername || '');

  // Initialize GA on mount
  useEffect(() => {
    initGA();
    // Fire initial pageview
    pageview(window.location.pathname);
  }, []);

  // Handle URL-based routing
  useEffect(() => {
    const handleRouteChange = () => {
      canonicalizeMisroutedFeedbackPath();
      const rawPath = window.location.pathname;
      let path = rawPath !== '/' && rawPath.endsWith('/') ? rawPath.slice(0, -1) : rawPath;
      if (!path.startsWith('/')) path = '/' + path;
      if (path === '/brand-deal-checker') {
        const q = window.location.search || '';
        window.history.replaceState({}, '', '/influencer-contract-checker' + q);
        path = '/influencer-contract-checker';
      }
      console.log('📍 [ROUTE DEBUG] Current path:', path);
      
      // Redirect community profile routes to profile
      if (path.startsWith('/community/profile/')) {
        window.history.replaceState({}, '', '/profile/edit');
        setCurrentPage('profile-edit');
        return;
      }
      
      // Map URL paths to page states
      switch (path) {
        case '':
        case '/':
          setCurrentPage('marketing-landing');
          break;
        case '/login':
          setCurrentPage('login');
          break;
        case '/contact':
          setCurrentPage('marketing-contact');
          break;
        case '/privacy':
          setCurrentPage('marketing-privacy');
          break;
        case '/terms':
          setCurrentPage('marketing-terms');
          break;
        case '/what-is-an-influencer-profile':
          setCurrentPage('marketing-what-is-influencer-profile');
          break;
        case '/create-influencer-profile':
          setCurrentPage('marketing-create-influencer-profile');
          break;
        case '/media-kit-guide':
          setCurrentPage('marketing-media-kit-guide');
          break;
        case '/influencer-contract-checker':
          setCurrentPage('marketing-brand-deal-checker');
          break;
        case '/forgot-password':
          setCurrentPage('forgot-password');
          break;
        case '/signup':
          setCurrentPage('signup');
          break;
        case '/onboarding':
          setCurrentPage('onboarding');
          break;
        case '/onboarding/step-1':
          setCurrentPage('onboarding/step-1');
          break;
        case '/onboarding/step-2':
          setCurrentPage('onboarding/step-2');
          break;
        case '/onboarding/step-3':
          setCurrentPage('onboarding/step-3');
          break;
        case '/select-role':
          setCurrentPage('select-role');
          break;
        case '/admin':
          // Redirect old admin route to Profile Builder
          window.history.replaceState({}, '', '/profile/edit');
          setCurrentPage('profile-edit');
          break;
        case '/dashboard':
          // Redirect legacy dashboard to Profile Builder
          window.history.replaceState({}, '', '/profile/edit');
          setCurrentPage('profile-edit');
          break;
        case '/profile/edit':
          setCurrentPage('profile-edit');
          break;
        case '/profile/contract-tools':
          setCurrentPage('profile-contract-tools');
          break;
        case '/feedback': {
          const search = window.location.search || '';
          const postParam = search ? new URLSearchParams(search).get('post') : null;
          const expandPostId = postParam && postParam.trim() ? postParam.trim() : null;
          setCurrentPage('feedback-feed');
          setFeedbackPostId(expandPostId);
          break;
        }
        case '/feedback/new':
          window.history.replaceState({}, '', '/feedback');
          setCurrentPage('feedback-feed');
          setFeedbackPostId(null);
          break;
        case '/settings':
          setCurrentPage('settings');
          break;
        case '/settings/email':
          setCurrentPage('email-unsubscribe');
          break;
        case '/email/unsubscribe':
          window.history.replaceState({}, '', '/settings/email');
          setCurrentPage('email-unsubscribe');
          break;
        case '/collabs':
        case '/community':
        case '/notifications':
          // Redirect removed features to profile
          window.history.replaceState({}, '', '/profile/edit');
          setCurrentPage('profile-edit');
          break;
        case '/brand/create-campaign':
          setCurrentPage('create-campaign');
          break;
        case '/brand/campaigns':
          setCurrentPage('my-campaigns');
          break;
        case '/brand/applications':
          setCurrentPage('brand-applications');
          break;
        case '/influencer/explore-campaigns':
        case '/explore-campaigns':
          setCurrentPage('explore-campaigns');
          break;
        case '/campaign-submitted':
          setCurrentPage('campaign-submitted');
          break;
        // LEGACY: Pending approval routes - no longer used after removing approval workflow
        // Keeping for backwards compatibility, but redirecting to Profile Builder
        case '/pending-approval':
        case '/influencer/pending-approval':
          window.history.replaceState({}, '', '/profile/edit');
          setCurrentPage('profile-edit'); // Redirect to Profile Builder
          break;
        case '/debug-auth':
          setCurrentPage('debug-auth');
          break;
        case '/test-auth':
          setCurrentPage('test-auth');
          break;
        case '/debug/appcheck':
          setCurrentPage('debug-appcheck');
          break;
        case '/onboarding/brand':
          setCurrentPage('onboarding/brand');
          break;
        case '/brand/dashboard':
          setCurrentPage('brand-dashboard');
          break;
        case '/brand/preferences':
          setCurrentPage('brand-preferences');
          break;
        case '/brand/profile':
          setCurrentPage('brand-profile');
          break;
        default:
          if (path.startsWith('/feedback/')) {
            const rest = path.slice(10);
            const q = rest.indexOf('?');
            const segment = q >= 0 ? rest.slice(0, q) : rest;
            if (segment === 'new') {
              window.history.replaceState({}, '', '/feedback');
              setCurrentPage('feedback-feed');
              setFeedbackPostId(null);
              break;
            }
            if (segment && !segment.includes('/')) {
              setCurrentPage('feedback-detail');
              setFeedbackPostId(segment);
              break;
            }
          }
          // Handle dynamic routes like /brand/campaigns/[campaignId]
          if (path.startsWith('/brand/campaigns/')) {
            const campaignId = path.split('/brand/campaigns/')[1];
            if (campaignId) {
              setCurrentPage('brand-campaign-detail');
              setSelectedCampaignId(campaignId);
              setSelectedBrandId(null);
              setSelectedInfluencerId(null);
              setSelectedApplicationId(null);
              setIsAdmin(false);
              break;
            }
          }
          // Handle dynamic routes like /brand/applications/[applicationId]
          if (path.startsWith('/brand/applications/')) {
            const applicationId = path.split('/brand/applications/')[1];
            if (applicationId) {
              setCurrentPage('application-detail');
              setSelectedApplicationId(applicationId);
              setSelectedBrandId(null);
              setSelectedInfluencerId(null);
              setSelectedCampaignId(null);
              setIsAdmin(false);
              break;
            }
          }
          // Handle dynamic routes like /signup-wizard?type=influencer
          if (path.startsWith('/signup-wizard')) {
            setCurrentPage('signup-wizard');
            const urlParams = new URLSearchParams(window.location.search);
            setQueryParams(urlParams.toString());
          } else if (path.startsWith('/brand-wizard')) {
            setCurrentPage('brand-wizard');
            const urlParams = new URLSearchParams(window.location.search);
            setQueryParams(urlParams.toString());
          } else if (path.startsWith('/u/')) {
            const parts = path.split('/').filter(Boolean);
            if (parts[2] === 'media-kit' && parts[1]) {
              setCurrentPage('media-kit');
            } else {
              const username = parts[1];
              if (username === 'email') {
                window.history.replaceState({}, '', '/settings/email');
                setCurrentPage('email-unsubscribe');
                setPublicUsername('');
              } else if (username) {
                setCurrentPage('public-profile');
                setPublicUsername(username);
              } else {
                setCurrentPage('login');
              }
            }
          } else if (path.endsWith('/media-kit')) {
            const parts = path.split('/').filter(Boolean);
            if (parts.length >= 2 && parts[0] !== 'u') {
              window.history.replaceState({}, '', '/u/' + parts[0] + '/media-kit');
            }
            setCurrentPage('media-kit');
          } else if (path !== '/' && path !== '/contact' && path !== '/privacy' && path !== '/terms' && path !== '/what-is-an-influencer-profile' && path !== '/create-influencer-profile' && path !== '/media-kit-guide' && path !== '/influencer-contract-checker' && !path.startsWith('/login') && !path.startsWith('/signup') && !path.startsWith('/onboarding') && !path.startsWith('/admin') && !path.startsWith('/brand') && !path.startsWith('/influencer') && !path.startsWith('/campaign') && !path.startsWith('/forgot-password') && !path.startsWith('/select-role') && !path.startsWith('/pending-approval') && !path.startsWith('/debug-auth') && !path.startsWith('/test-auth') && !path.startsWith('/debug') && !path.startsWith('/settings') && !path.startsWith('/collabs') && !path.startsWith('/community') && !path.startsWith('/notifications') && !path.startsWith('/feedback') && !path.startsWith('/u/')) {
            const username = path.split('/').filter(Boolean)[0];
            if (username && username !== 'dashboard' && username !== 'profile' && username !== 'u') {
              window.history.replaceState({}, '', '/u/' + username);
              setCurrentPage('public-profile');
              setPublicUsername(username);
            } else {
              setCurrentPage('login');
            }
          } else {
            setCurrentPage('login');
          }
      }
    };

    // Set initial route
    handleRouteChange();

    // Listen for browser back/forward
    window.addEventListener('popstate', handleRouteChange);
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  // Navigation handler
  const navigateTo = (page, id = null, options = {}) => {
    const rawTarget = page.startsWith('/') ? page.slice(1) : page;
    let targetPage =
      rawTarget === 'profile/edit'
        ? 'profile-edit'
        : rawTarget === 'profile/contract-tools'
          ? 'profile-contract-tools'
          : rawTarget;
    // Normalize path-style routes so renderCurrentPage switch matches (fixes post-login brand landing)
    if (targetPage === 'brand/dashboard') targetPage = 'brand-dashboard';
    if (targetPage === 'brand/profile') targetPage = 'brand-profile';
    if (targetPage === 'brand/preferences') targetPage = 'brand-preferences';
    if (targetPage === 'feedback/new') targetPage = 'feedback-new';
    if (targetPage === 'feedback') targetPage = 'feedback-feed';
    const historyMethod = options.replace ? 'replaceState' : 'pushState';
    // Home / marketing landing (`navigateTo('/')` yields empty rawTarget)
    if (targetPage === '' || targetPage === 'marketing-landing' || targetPage === 'home') {
      setCurrentPage('marketing-landing');
      setQueryParams('');
      setSelectedBrandId(null);
      setSelectedInfluencerId(null);
      setSelectedCampaignId(null);
      setSelectedApplicationId(null);
      setFeedbackPostId(null);
      window.history[historyMethod]({}, '', '/');
      pageview('/');
      setIsAdmin(false);
      console.log('🧭 Navigated to: marketing-landing');
      return;
    }
    // Handle explore-campaigns navigation
    if (targetPage === 'explore-campaigns') {
      setCurrentPage('explore-campaigns');
      const url = '/influencer/explore-campaigns';
      window.history[historyMethod]({}, '', url);
      pageview(url);
      return;
    }
    // Canonical creator profile: /u/[username]
    if (targetPage.startsWith('u/')) {
      const username = targetPage.slice(2).split('/')[0];
      if (username) {
        setCurrentPage('public-profile');
        setPublicUsername(username);
        const url = '/u/' + username;
        window.history[historyMethod]({}, '', url);
        pageview(url);
        return;
      }
    }
    // Handle brand detail navigation
    if (targetPage === 'brand-detail' && id) {
      setCurrentPage('brand-detail');
      setSelectedBrandId(id);
      setSelectedInfluencerId(null);
      setQueryParams('');
      // Update URL
      const url = `/admin/brands/${id}`;
      window.history[historyMethod]({}, '', url);
      pageview(url);
      setIsAdmin(true);
      console.log('🧭 Navigated to brand detail:', id);
      return;
    }
    
    // Handle influencer detail navigation
    if (targetPage === 'influencer-detail' && id) {
      setCurrentPage('influencer-detail');
      setSelectedInfluencerId(id);
      setSelectedBrandId(null);
      setSelectedCampaignId(null);
      setQueryParams('');
      // Update URL
      const url = `/admin/influencers/${id}`;
      window.history[historyMethod]({}, '', url);
      pageview(url);
      setIsAdmin(true);
      console.log('🧭 Navigated to influencer detail:', id);
      return;
    }
    
    // Handle campaign detail navigation
    if (targetPage === 'campaign-detail' && id) {
      setCurrentPage('campaign-detail');
      setSelectedCampaignId(id);
      setSelectedBrandId(null);
      setSelectedInfluencerId(null);
      setSelectedApplicationId(null);
      setQueryParams('');
      // Update URL
      const url = `/admin/campaigns/${id}`;
      window.history[historyMethod]({}, '', url);
      pageview(url);
      setIsAdmin(true);
      console.log('🧭 Navigated to campaign detail:', id);
      return;
    }
    
    // Handle application detail navigation
    if (targetPage === 'application-detail' && id) {
      setCurrentPage('application-detail');
      setSelectedApplicationId(id);
      setSelectedBrandId(null);
      setSelectedInfluencerId(null);
      setSelectedCampaignId(null);
      setQueryParams('');
      // Update URL
      const url = `/brand/applications/${id}`;
      window.history[historyMethod]({}, '', url);
      pageview(url);
      setIsAdmin(false);
      console.log('🧭 Navigated to application detail:', id);
      return;
    }
    
    // Handle brand campaign detail navigation
    if (targetPage === 'brand-campaign-detail' && id) {
      setCurrentPage('brand-campaign-detail');
      setSelectedCampaignId(id);
      setSelectedBrandId(null);
      setSelectedInfluencerId(null);
      setSelectedApplicationId(null);
      setQueryParams('');
      // Update URL
      const url = `/brand/campaigns/${id}`;
      window.history[historyMethod]({}, '', url);
      pageview(url);
      setIsAdmin(false);
      console.log('🧭 Navigated to brand campaign detail:', id);
      return;
    }

    if (targetPage === 'feedback-detail' && id) {
      setCurrentPage('feedback-detail');
      setFeedbackPostId(id);
      setSelectedBrandId(null);
      setSelectedInfluencerId(null);
      setSelectedCampaignId(null);
      setSelectedApplicationId(null);
      setQueryParams('');
      const url = `/feedback/${id}`;
      window.history[historyMethod]({}, '', url);
      pageview(url);
      setIsAdmin(false);
      return;
    }
    if (targetPage === 'feedback-feed') {
      setCurrentPage('feedback-feed');
      const expandPostId = id && typeof id === 'string' ? id : null;
      setFeedbackPostId(expandPostId);
      setSelectedBrandId(null);
      setSelectedInfluencerId(null);
      setSelectedCampaignId(null);
      setSelectedApplicationId(null);
      setQueryParams('');
      const feedbackUrl = expandPostId ? `/feedback?post=${encodeURIComponent(expandPostId)}` : '/feedback';
      window.history[historyMethod]({}, '', feedbackUrl);
      pageview(feedbackUrl);
      setIsAdmin(false);
      return;
    }
    if (targetPage === 'feedback-new') {
      setCurrentPage('feedback-feed');
      setFeedbackPostId(null);
      setSelectedBrandId(null);
      setSelectedInfluencerId(null);
      setSelectedCampaignId(null);
      setSelectedApplicationId(null);
      setQueryParams('');
      window.history[historyMethod]({}, '', '/feedback');
      pageview('/feedback');
      setIsAdmin(false);
      return;
    }

    // Handle query parameters
    if (targetPage.includes('?')) {
      const [pageName, params] = targetPage.split('?');
      // Redirect community routes to profile
      if (pageName === 'community') {
        setCurrentPage('profile-edit');
        const url = '/profile/edit';
        window.history.pushState({}, '', url);
        pageview(url);
        return;
      }
      setCurrentPage(pageName);
      setQueryParams(params);
      // Update URL
      const url = `/${pageName}?${params}`;
      window.history[historyMethod]({}, '', url);
      pageview(url);
    } else {
      setCurrentPage(targetPage);
      setQueryParams('');
      setSelectedBrandId(null);
      setSelectedInfluencerId(null);
      setSelectedCampaignId(null);
      setSelectedApplicationId(null);
      setFeedbackPostId(null);
      // Update URL
      let url;
      if (targetPage === 'login') {
        url = '/login';
      } else if (targetPage === 'forgot-password') {
        url = '/forgot-password';
      } else if (targetPage === 'dashboard-new') {
        // Legacy dashboard route - redirect to Profile Builder
        url = '/profile/edit';
        setCurrentPage('profile-edit');
      } else if (targetPage === 'profile-edit') {
        url = '/profile/edit';
      } else if (targetPage === 'profile-contract-tools') {
        url = '/profile/contract-tools';
      } else if (targetPage === 'debug-appcheck') {
        url = '/debug/appcheck';
      } else if (targetPage === 'brand-dashboard') {
        url = '/brand/dashboard';
      } else if (targetPage === 'brand-profile') {
        url = '/brand/profile';
      } else if (targetPage === 'brand-preferences') {
        url = '/brand/preferences';
      } else if (targetPage === 'email-unsubscribe') {
        url = '/settings/email';
      } else {
        url = `/${targetPage}`;
      }
      window.history[historyMethod]({}, '', url);
      pageview(url);
    }
    
    // Set admin state
    if (targetPage === 'admin' || targetPage === 'brand-detail' || targetPage === 'influencer-detail') {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
    
    console.log('🧭 Navigated to:', targetPage);
  };

  // Render current page based on state
  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'marketing-landing':
        return (
          <MarketingLayout>
            <LandingPage />
          </MarketingLayout>
        );
      case 'marketing-contact':
        return (
          <MarketingLayout>
            <ContactPage />
          </MarketingLayout>
        );
      case 'marketing-privacy':
        return (
          <MarketingLayout>
            <PrivacyPage />
          </MarketingLayout>
        );
      case 'marketing-terms':
        return (
          <MarketingLayout>
            <TermsPage />
          </MarketingLayout>
        );
      case 'marketing-what-is-influencer-profile':
        return (
          <MarketingLayout>
            <WhatIsInfluencerProfilePage />
          </MarketingLayout>
        );
      case 'marketing-create-influencer-profile':
        return (
          <MarketingLayout>
            <CreateInfluencerProfilePage />
          </MarketingLayout>
        );
      case 'marketing-media-kit-guide':
        return (
          <MarketingLayout>
            <MediaKitGuidePage />
          </MarketingLayout>
        );
      case 'marketing-brand-deal-checker':
        return (
          <MarketingLayout>
            <BrandDealCheckerPage />
          </MarketingLayout>
        );
      case 'select-role':
        return <SelectRole onNavigate={navigateTo} />;
      case 'signup':
        return <Signup onNavigate={navigateTo} />;
      case 'signup-wizard':
        return <InfluencerWizard onNavigate={navigateTo} queryParams={queryParams} />;
      case 'brand-wizard':
        return <BrandWizard onNavigate={navigateTo} queryParams={queryParams} />;
      case 'login':
        return <Login onNavigate={navigateTo} />;
      case 'forgot-password':
        return <ForgotPassword onNavigate={navigateTo} />;
      case 'onboarding':
        return <Onboarding onNavigate={navigateTo} />;
      case 'onboarding/step-1':
        return <OnboardingStep1 onNavigate={navigateTo} />;
      case 'onboarding/step-2':
        return <OnboardingStep2 onNavigate={navigateTo} />;
      case 'onboarding/step-3':
        return <OnboardingStep3 onNavigate={navigateTo} />;
      case 'onboarding/brand':
        return <BrandOnboarding onNavigate={navigateTo} />;
      case 'brand-dashboard':
        return <BrandDashboard onNavigate={navigateTo} />;
      case 'brand-preferences':
        return <BrandPreferences onNavigate={navigateTo} />;
      case 'brand-profile':
        return <BrandProfile onNavigate={navigateTo} />;
      case 'dashboard':
        // Legacy dashboard route - redirect to Profile Builder
        navigateTo('profile-edit');
        return null;
      case 'dashboard-new':
        // Legacy dashboard route - redirect to Profile Builder
        // This case should rarely be hit since we redirect in route handler,
        // but if it is, redirect to profile-edit
        navigateTo('profile-edit');
        return null;
      case 'profile-edit':
        return (
          <RoleGuard onNavigate={navigateTo} allowedRoles={['influencer', 'creator']}>
            <ProfileEditor onNavigate={navigateTo} />
          </RoleGuard>
        );
      case 'feedback-feed':
      case 'feedback-new':
        return (
          <RoleGuard onNavigate={navigateTo} allowedRoles={['influencer', 'creator']}>
            <FeedbackFeedPage
              onNavigate={navigateTo}
              initialExpandPostId={currentPage === 'feedback-feed' ? feedbackPostId : null}
            />
          </RoleGuard>
        );
      case 'feedback-detail':
        return (
          <RoleGuard onNavigate={navigateTo} allowedRoles={['influencer', 'creator']}>
            <FeedbackPostDetailPage
              postId={feedbackPostId}
              onNavigate={navigateTo}
            />
          </RoleGuard>
        );
      case 'profile-contract-tools':
        return (
          <RoleGuard onNavigate={navigateTo} allowedRoles={['influencer', 'creator']}>
            <ContractToolsPage onNavigate={navigateTo} />
          </RoleGuard>
        );
      case 'public-profile':
        return <PublicProfileWithLayout username={publicUsername} onNavigate={navigateTo} />;
      case 'media-kit':
        return <MediaKit onNavigate={navigateTo} />;
      case 'admin':
        // Redirect old admin dashboard to Profile Builder
        // This case should rarely be hit since we redirect in route handler,
        // but if it is, redirect to profile-edit
        navigateTo('profile-edit');
        return null;
      case 'brand-detail':
        return isAdmin && selectedBrandId ? <BrandDetail brandId={selectedBrandId} onNavigate={navigateTo} /> : <Login onNavigate={navigateTo} />;
      case 'influencer-detail':
        return isAdmin && selectedInfluencerId ? <InfluencerDetail influencerId={selectedInfluencerId} onNavigate={navigateTo} /> : <Login onNavigate={navigateTo} />;
      case 'campaign-detail':
        return isAdmin && selectedCampaignId ? <CampaignDetail campaignId={selectedCampaignId} onNavigate={navigateTo} /> : <Login onNavigate={navigateTo} />;
      // LEGACY: Pending approval pages - no longer used after removing approval workflow
      // These pages now redirect to dashboard automatically
      case 'pending-approval':
        return <PendingApproval onNavigate={navigateTo} />;
      case 'influencer-pending-approval':
        return <InfluencerPendingApproval onNavigate={navigateTo} />;
      case 'create-campaign':
        return (
          <RoleGuard onNavigate={navigateTo}>
            <CreateCampaign onNavigate={navigateTo} />
          </RoleGuard>
        );
      case 'my-campaigns':
        return (
          <RoleGuard onNavigate={navigateTo}>
            <MyCampaigns onNavigate={navigateTo} />
          </RoleGuard>
        );
      case 'brand-campaign-detail':
        return (
          <RoleGuard onNavigate={navigateTo}>
            <BrandCampaignDetail campaignId={selectedCampaignId} onNavigate={navigateTo} />
          </RoleGuard>
        );
      case 'brand-applications':
        return (
          <RoleGuard onNavigate={navigateTo}>
            <BrandApplications onNavigate={navigateTo} />
          </RoleGuard>
        );
      case 'influencer-applications':
        return (
          <RoleGuard onNavigate={navigateTo}>
            <InfluencerApplications onNavigate={navigateTo} />
          </RoleGuard>
        );
      case 'explore-campaigns':
        return (
          <RoleGuard onNavigate={navigateTo}>
            <ExploreCampaigns onNavigate={navigateTo} />
          </RoleGuard>
        );
      case 'application-detail':
        return (
          <RoleGuard onNavigate={navigateTo}>
            <ApplicationDetail applicationId={selectedApplicationId} onNavigate={navigateTo} />
          </RoleGuard>
        );
            case 'campaign-submitted':
              return (
                <RoleGuard onNavigate={navigateTo}>
                  <CampaignSubmitted onNavigate={navigateTo} />
                </RoleGuard>
              );
            case 'settings':
              return (
                <RoleGuard onNavigate={navigateTo}>
                  <Settings onNavigate={navigateTo} />
                </RoleGuard>
              );
            case 'email-unsubscribe':
              return <EmailUnsubscribePage onNavigate={navigateTo} />;
            case 'collabs':
            case 'community':
            case 'notifications':
              // Redirect removed features to profile
              navigateTo('profile-edit');
              return null;
            case 'debug-auth':
              return <DebugAuth onNavigate={navigateTo} />;
            case 'test-auth':
              return <TestAuth onNavigate={navigateTo} />;
            default:
              // Redirect any unmatched routes to profile (for removed features)
              if (currentPage.startsWith('community-profile-') || 
                  currentPage.startsWith('community') ||
                  currentPage === 'notifications' ||
                  currentPage === 'collabs') {
                navigateTo('profile-edit');
                return null;
              }
              console.log('📍 [ROUTE DEBUG] No matching route, defaulting to login. currentPage:', currentPage);
              return <Login onNavigate={navigateTo} />;
    }
  };

  // Debug logging
  console.log('🔍 App render - currentPage:', currentPage, 'queryParams:', queryParams);

  return (
    <div className="App">
      <ClientFirebaseBootstrap />
      {/* Main application layout */}
      {/* Navigation and sidebar components */}
      {/* Route handling for different pages */}
      {/* Authentication state management */}
      {renderCurrentPage()}
    </div>
  );
};

export default App;
